// src/pages/markets/board/card/trendGeometry.ts
//
// Was private to CardTrendChart. The marker overlay has to land on the same
// scale as the line, so the scale is its own module now.

import type { SeriesPoint } from '../api/boardTypes';

export const VB = 100;
const PAD = 0.06;

export interface Geometry {
    path: string;
    area: string;
    referenceY: number | null;
    last: number | null;
    min: number;
    max: number;
    firstTs: string | null;
    lastTs: string | null;
    firstMs: number;
    lastMs: number;
}

type UsablePoint = { t: string; close: number };

function usablePoints(points: SeriesPoint[]): UsablePoint[] {
    return points.filter(p => p.close !== null) as UsablePoint[];
}

/** The window the series actually covers, independent of the y scale. */
export function seriesSpanMs(
    points: SeriesPoint[] | undefined | null
): { firstMs: number; lastMs: number } | null {
    if (!points) return null;
    const usable = usablePoints(points);
    if (usable.length < 2) return null;
    return {
        firstMs: Date.parse(usable[0].t),
        lastMs: Date.parse(usable[usable.length - 1].t),
    };
}

export function buildGeometry(
    points: SeriesPoint[],
    reference: number | null,
    extraValues: number[] = []
): Geometry | null {
    const usable = usablePoints(points);
    if (usable.length < 2) return null;

    const firstMs = Date.parse(usable[0].t);
    const lastMs = Date.parse(usable[usable.length - 1].t);
    // The path has to share the markers' x scale, or a series with a dropped
    // bucket puts a dot where the line is not. Index spacing is the fallback
    // for timestamps that will not parse.
    const timeScaled = lastMs > firstMs;

    const values = usable.map(p => p.close);
    let min = Math.min(...values);
    let max = Math.max(...values);
    if (reference !== null) {
        min = Math.min(min, reference);
        max = Math.max(max, reference);
    }
    for (const v of extraValues) {
        if (!Number.isFinite(v)) continue;
        min = Math.min(min, v);
        max = Math.max(max, v);
    }

    const span = max - min || 0.02;
    min -= span * PAD;
    max += span * PAD;

    const x = (p: UsablePoint, i: number) =>
        timeScaled
            ? xForMs(Date.parse(p.t), firstMs, lastMs)
            : (i / (usable.length - 1)) * VB;
    const y = (v: number) => VB - ((v - min) / (max - min)) * VB;

    const path = usable
        .map(
            (p, i) =>
                `${i === 0 ? 'M' : 'L'}${x(p, i).toFixed(2)},${y(p.close).toFixed(2)}`
        )
        .join(' ');
    const area = `${path} L${VB},${VB} L0,${VB} Z`;

    return {
        path,
        area,
        referenceY: reference === null ? null : y(reference),
        last: values[values.length - 1],
        min,
        max,
        firstTs: usable[0].t,
        lastTs: usable[usable.length - 1].t,
        firstMs,
        lastMs,
    };
}

export function yForValue(geo: Geometry, v: number): number {
    return VB - ((v - geo.min) / (geo.max - geo.min)) * VB;
}

function xForMs(ms: number, firstMs: number, lastMs: number): number {
    if (!(lastMs > firstMs)) return VB;
    const raw = ((ms - firstMs) / (lastMs - firstMs)) * VB;
    return Math.max(0, Math.min(VB, raw));
}

export function xForTime(geo: Geometry, ms: number): number {
    return xForMs(ms, geo.firstMs, geo.lastMs);
}
